// src/portal/PortalMapPanel.jsx
//
// PORTED from the mock's `.map-panel` chrome — the head (file chip, revision
// chip, feature/CRS/size line, Download), the legend and source overlaid on the
// media box, and the foot with the scalebar and the coordinate/zoom readout.
//
// THE MEDIA BOX ITSELF IS UNCHANGED. The mock draws an <img> with an <svg>
// overlay and says so; here it is the MapLibre canvas the two map components
// already own. This file wraps it and nothing else — no map behaviour moves.
import { measureFeature, geomWord } from "./portalMeasure";

const SWATCH = {
  pad:      "bg-[rgb(var(--geom-pad))]",
  road:     "bg-[rgb(var(--geom-road))]",
  other:    "bg-[rgb(var(--geom-other))]",
};
const fmtBytes = (n) => {
  if (!Number.isFinite(n) || n <= 0) return null;
  const mb = n / (1024 * 1024);
  return mb >= 1 ? `${mb.toFixed(1)} MB` : `${Math.round(n / 1024)} KB`;
};

export default function PortalMapPanel({
  revision, features = [], imagery, readout, onExport, children,
}) {
  const meta = [
    revision?.feature_count != null
      ? `${revision.feature_count} feature${revision.feature_count === 1 ? "" : "s"}` : null,
    revision?.stored_crs ?? revision?.declared_crs ?? null,
    fmtBytes(revision?.artifact_size_bytes),
  ].filter(Boolean).join(" · ");

  return (
    <section className="op-panel" aria-label="Plan" data-testid="map-panel">
      <div className="op-panel-head">
        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {revision?.artifact_filename && (
            <span className="op-chip op-mono truncate" data-testid="map-file-chip">
              {revision.artifact_filename}
            </span>
          )}
          {revision?.revision != null && (
            <span className="op-chip op-chip--accent" data-testid="map-revision-chip">
              REVISION {revision.revision}
            </span>
          )}
          {meta && <span className="op-meta" data-testid="map-meta">{meta}</span>}
        </div>
        {onExport && (
          <button type="button" onClick={onExport} className="op-btn op-btn--sm" data-testid="map-export">
            Download
          </button>
        )}
      </div>

      {/* The media box. MapLibre lives here; the overlays are absolutely
          positioned over it, exactly as the mock layers them. */}
      <div className="relative h-[28rem] shrink-0">
        {children}

        {features.length > 0 && (
          <div
            data-testid="map-legend"
            className="pointer-events-none absolute bottom-2 right-2 flex flex-col gap-1 rounded-[--r-sm] bg-[rgb(var(--chrome)/0.78)] px-2 py-1.5"
          >
            {features.map((f) => {
              // COMPUTED, NOT STUBBED. turf over the 4326 geometry route 6
              // already returns — see portalMeasure.js for what the number is
              // and is not. A feature whose geometry cannot be measured shows
              // its name without a figure rather than a placeholder.
              const size = measureFeature(f);
              return (
                <span key={f.ordinal} className="flex items-center gap-1.5 op-label whitespace-nowrap">
                  <span className={`h-2 w-2 shrink-0 rounded-[2px] ${SWATCH[f.submitter_tag] ?? "bg-[rgb(var(--geom-untagged))]"}`} />
                  #{f.ordinal} {f.submitter_tag ?? "untagged"} · {geomWord(f)}
                  {size ? ` · ${size}` : ""}
                </span>
              );
            })}
          </div>
        )}

        {imagery?.dataset_key && (
          <span
            data-testid="map-source"
            className="pointer-events-none absolute bottom-2 left-2 op-label rounded-[--r-sm] bg-[rgb(var(--chrome)/0.78)] px-2 py-1"
          >
            {[imagery.dataset_key, imagery.lod,
              imagery.acquisition_date ? `captured ${String(imagery.acquisition_date).slice(0, 10)}` : null]
              .filter(Boolean).join(" · ")}
          </span>
        )}
      </div>

      <div className="flex h-10 shrink-0 items-center justify-between gap-4 border-t border-bg-border px-3.5">
        <span className="flex items-center gap-2.5" aria-hidden="true" data-testid="map-scalebar">
          <span className="flex h-[9px] items-end">
            <span className="h-[9px] w-px bg-fg-secondary/60" />
            <span className="h-px w-[62px] self-end bg-fg-secondary/60" />
            <span className="h-[9px] w-px bg-fg-secondary/60" />
          </span>
        </span>
        <span className="op-label" data-testid="map-readout">{readout ?? ""}</span>
      </div>
    </section>
  );
}
